import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, ExclamationTriangleIcon, CheckIcon } from '@heroicons/react/24/outline';

const EMPLOYMENT_TYPES = [
  { id: 'full_time', label: 'Full-time' },
  { id: 'part_time', label: 'Part-time' },
  { id: 'probation', label: '90-day Probation' },
];

export default function AttendanceRulesPage() {
  const [rules, setRules] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        api.get('/attendance/rules'),
        api.get('/attendance/thresholds'),
      ]);
      setRules(r.data);
      setThresholds(t.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateRule = async (id, patch) => {
    try {
      await api.patch(`/attendance/rules/${id}`, patch);
      load();
    } catch { toast.error('Failed to save'); }
  };

  const updateThreshold = async (id, patch) => {
    try {
      await api.patch(`/attendance/thresholds/${id}`, patch);
      load();
    } catch { toast.error('Failed to save'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;

  return (
    <div className="animate-slide-up">
      <Link to="/attendance" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-orange-500 mb-3">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Attendance
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Attendance Rules & Thresholds</h1>
        <p className="text-gray-500 text-sm mt-1">
          These values come from the BBC Training Manual. Edit them here to update the policy without redeploying.
        </p>
      </div>

      {/* Point Rules */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Point Values per Infraction</h2>
        <p className="text-xs text-gray-500 mb-4">Disable a rule by toggling Active off. Negative values are credits (e.g. perfect-month).</p>
        <div className="space-y-2">
          {rules.map(r => <RuleRow key={r.id} rule={r} onSave={(p) => updateRule(r.id, p)} />)}
        </div>
      </div>

      {/* Thresholds */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Status Thresholds</h2>
        <p className="text-xs text-gray-500 mb-4">When a staff member's balance crosses one of these, the system fires a notification email and shows the action label on the dashboard.</p>
        <div className="space-y-6">
          {EMPLOYMENT_TYPES.map(et => {
            const ts = thresholds.filter(t => t.employment_type === et.id);
            return (
              <div key={et.id}>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">{et.label}</h3>
                <div className="space-y-2">
                  {ts.map(t => <ThresholdRow key={t.id} threshold={t} onSave={(p) => updateThreshold(t.id, p)} />)}
                  {ts.length === 0 && <p className="text-xs text-gray-400">(no thresholds defined)</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function RuleRow({ rule, onSave }) {
  const [points, setPoints] = useState(rule.points);
  const [label, setLabel] = useState(rule.label);
  const [active, setActive] = useState(rule.is_active);
  const dirty = points !== rule.points || label !== rule.label || active !== rule.is_active;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${dirty ? 'border-orange-200 bg-orange-50/30' : 'border-gray-100'}`}>
      <input value={label} onChange={(e) => setLabel(e.target.value)}
        className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-orange-500 focus:outline-none bg-white" />
      <input type="number" step="0.5" value={points} onChange={(e) => setPoints(Number(e.target.value))}
        className="w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-center bg-white focus:border-orange-500 focus:outline-none" />
      <span className="text-xs text-gray-500 w-12">pts</span>
      <label className="inline-flex items-center gap-1 text-xs text-gray-600">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-orange-500" />
        Active
      </label>
      <button disabled={!dirty} onClick={() => onSave({ points, label, is_active: active })}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
          dirty ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}>
        <CheckIcon className="w-3.5 h-3.5 inline mr-1" />Save
      </button>
    </div>
  );
}

function ThresholdRow({ threshold, onSave }) {
  const [points, setPoints] = useState(threshold.points_at);
  const [actionLabel, setActionLabel] = useState(threshold.action_label);
  const dirty = Number(points) !== Number(threshold.points_at) || actionLabel !== threshold.action_label;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${dirty ? 'border-orange-200 bg-orange-50/30' : 'border-gray-100'}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        threshold.color === 'red' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
      }`}>
        <ExclamationTriangleIcon className="w-4 h-4" />
      </div>
      <span className="text-xs text-gray-500">at</span>
      <input type="number" step="0.5" value={points} onChange={(e) => setPoints(Number(e.target.value))}
        className="w-16 rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-center bg-white focus:border-orange-500 focus:outline-none" />
      <span className="text-xs text-gray-500">pts →</span>
      <input value={actionLabel} onChange={(e) => setActionLabel(e.target.value)}
        className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm bg-white focus:border-orange-500 focus:outline-none" />
      <button disabled={!dirty} onClick={() => onSave({ points_at: points, action_label: actionLabel })}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
          dirty ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}>
        <CheckIcon className="w-3.5 h-3.5 inline mr-1" />Save
      </button>
    </div>
  );
}
